import { type Static, Type } from "@sinclair/typebox";
import { Value } from "@sinclair/typebox/value";
import {
  GroupBuyManualOperatorDecisionSchema,
  GroupBuyOnlineOperatorDecisionSchema,
} from "./group-buy-operator-decision.js";
import {
  GroupBuyRedemptionNumberSchema,
  isGroupBuyRedemptionNumber,
} from "./group-buy-redemption.js";
import {
  isTrackedSaleInventoryCostSnapshot,
  TrackedSaleInventoryCostSnapshotSchema,
} from "./sale-inventory.js";

const GroupBuyInventoryCostSnapshotIdentityFields = {
  skuId: Type.String({ minLength: 1, maxLength: 64 }),
  productVersion: Type.Integer({ minimum: 1, maximum: Number.MAX_SAFE_INTEGER }),
  quantity: Type.Integer({ minimum: 1, maximum: 99 }),
};

export const LegacyGroupBuyInventoryCostSnapshotSchema = Type.Union([
  Type.Object(
    {
      ...GroupBuyInventoryCostSnapshotIdentityFields,
      costSnapshotKnown: Type.Literal(true),
      unitCostFen: Type.Integer({ minimum: 0, maximum: 100_000_000 }),
    },
    { additionalProperties: false },
  ),
  Type.Object(
    {
      ...GroupBuyInventoryCostSnapshotIdentityFields,
      costSnapshotKnown: Type.Literal(false),
      unitCostFen: Type.Null(),
    },
    { additionalProperties: false },
  ),
]);
export type LegacyGroupBuyInventoryCostSnapshot = Static<
  typeof LegacyGroupBuyInventoryCostSnapshotSchema
>;

export const CurrentGroupBuyInventoryCostSnapshotSchema = Type.Union([
  Type.Object(
    {
      snapshotVersion: Type.Literal(2),
      ...GroupBuyInventoryCostSnapshotIdentityFields,
      inventoryManaged: Type.Literal(true),
      inventoryCostSnapshot: TrackedSaleInventoryCostSnapshotSchema,
    },
    { additionalProperties: false },
  ),
  Type.Object(
    {
      snapshotVersion: Type.Literal(2),
      ...GroupBuyInventoryCostSnapshotIdentityFields,
      inventoryManaged: Type.Literal(false),
      inventoryCostSnapshot: Type.Null(),
    },
    { additionalProperties: false },
  ),
]);
export type CurrentGroupBuyInventoryCostSnapshot = Static<
  typeof CurrentGroupBuyInventoryCostSnapshotSchema
>;

export const GroupBuyInventoryCostSnapshotSchema = Type.Union([
  LegacyGroupBuyInventoryCostSnapshotSchema,
  CurrentGroupBuyInventoryCostSnapshotSchema,
]);
export type GroupBuyInventoryCostSnapshot = Static<typeof GroupBuyInventoryCostSnapshotSchema>;

export function isCurrentGroupBuyInventoryCostSnapshot(
  value: unknown,
): value is CurrentGroupBuyInventoryCostSnapshot {
  if (!Value.Check(CurrentGroupBuyInventoryCostSnapshotSchema, value)) return false;
  const snapshot = value as CurrentGroupBuyInventoryCostSnapshot;
  if (!snapshot.inventoryManaged) return snapshot.inventoryCostSnapshot === null;
  return (
    isTrackedSaleInventoryCostSnapshot(snapshot.inventoryCostSnapshot) &&
    snapshot.inventoryCostSnapshot.skuId === snapshot.skuId &&
    snapshot.inventoryCostSnapshot.productVersion === snapshot.productVersion &&
    snapshot.inventoryCostSnapshot.soldBaseQuantity === snapshot.quantity
  );
}

export const GroupBuyFulfillmentInventoryEffectSchema = Type.Object(
  {
    skuId: Type.String({ minLength: 1, maxLength: 64 }),
    productName: Type.String({ minLength: 1, maxLength: 80 }),
    quantity: Type.Integer({ minimum: 1, maximum: 99 }),
    inventoryManaged: Type.Boolean(),
    inventoryQuantityBefore: Type.Union([
      Type.Integer({ minimum: 0, maximum: Number.MAX_SAFE_INTEGER }),
      Type.Null(),
    ]),
    inventoryQuantityAfter: Type.Union([
      Type.Integer({ minimum: 0, maximum: Number.MAX_SAFE_INTEGER }),
      Type.Null(),
    ]),
  },
  { additionalProperties: false },
);
export type GroupBuyFulfillmentInventoryEffect = Static<
  typeof GroupBuyFulfillmentInventoryEffectSchema
>;

export const GroupBuyFulfillmentRecordSchema = Type.Object(
  {
    id: Type.String({ minLength: 1 }),
    orderId: Type.String({ minLength: 1 }),
    sessionId: Type.String({ minLength: 1 }),
    tableId: Type.String({ minLength: 1 }),
    tableNumber: Type.String({ minLength: 1 }),
    businessDate: Type.String({ pattern: "^\\d{4}-\\d{2}-\\d{2}$" }),
    channelSnapshot: Type.Object(
      {
        id: Type.String({ minLength: 1 }),
        provider: Type.Union([Type.Literal("MEITUAN"), Type.Literal("DOUYIN")]),
        displayName: Type.String({ minLength: 1 }),
        version: Type.Integer({ minimum: 1 }),
        fulfillmentMode: Type.Union([
          Type.Literal("MANUAL_PLATFORM_APP"),
          Type.Literal("ONLINE_PROVIDER"),
        ]),
        officialIntegration: Type.Union([
          Type.Literal("NOT_VERIFIED"),
          Type.Literal("SANDBOX"),
          Type.Literal("STORE_ACCEPTED"),
        ]),
      },
      { additionalProperties: false },
    ),
    packageSnapshot: Type.Object(
      {
        id: Type.String({ minLength: 1 }),
        name: Type.String({ minLength: 1 }),
        version: Type.Integer({ minimum: 1 }),
        includedMinutes: Type.Integer({ minimum: 0, maximum: 1_440 }),
        components: Type.Array(
          Type.Object(
            {
              kind: Type.Union([Type.Literal("PRODUCT"), Type.Literal("SERVICE")]),
              referenceId: Type.Union([Type.String({ minLength: 1 }), Type.Null()]),
              name: Type.String({ minLength: 1 }),
              quantity: Type.Integer({ minimum: 1, maximum: 99 }),
            },
            { additionalProperties: false },
          ),
          { maxItems: 30 },
        ),
      },
      { additionalProperties: false },
    ),
    inventoryEffects: Type.Optional(
      Type.Array(GroupBuyFulfillmentInventoryEffectSchema, { maxItems: 30 }),
    ),
    platformReference: Type.String({ minLength: 4, maxLength: 64 }),
    redemptionId: Type.Optional(Type.String({ minLength: 12, maxLength: 128 })),
    redemptionNumber: Type.Optional(GroupBuyRedemptionNumberSchema),
    redemptionEntryMode: Type.Optional(
      Type.Union([Type.Literal("ONLINE"), Type.Literal("OFFLINE_PRECHECK")]),
    ),
    voucherCoveredFen: Type.Integer({ minimum: 0, maximum: 100_000_000 }),
    customerSupplementFen: Type.Integer({ minimum: 0, maximum: 100_000_000 }),
    expectedSettlementFen: Type.Integer({ minimum: 0, maximum: 100_000_000 }),
    expectedFeeFen: Type.Integer({ minimum: 0, maximum: 100_000_000 }),
    expectedSubsidyFen: Type.Integer({ minimum: 0, maximum: 100_000_000 }),
    actualSettlementFen: Type.Union([
      Type.Integer({ minimum: 0, maximum: 100_000_000 }),
      Type.Null(),
    ]),
    actualFeeFen: Type.Union([Type.Integer({ minimum: 0, maximum: 100_000_000 }), Type.Null()]),
    actualSubsidyFen: Type.Union([Type.Integer({ minimum: 0, maximum: 100_000_000 }), Type.Null()]),
    reconciliationOutcome: Type.Union([
      Type.Literal("PENDING"),
      Type.Literal("CONFIRMED"),
      Type.Literal("NOT_SETTLED"),
      Type.Literal("AMOUNT_MISMATCH"),
      Type.Literal("DUPLICATE_RECORD"),
    ]),
    note: Type.String({ maxLength: 200 }),
    reconciliationNote: Type.Union([Type.String({ maxLength: 500 }), Type.Null()]),
    coverageStartedAt: Type.String({ format: "date-time" }),
    coverageEndsAt: Type.String({ format: "date-time" }),
    fulfilledAt: Type.String({ format: "date-time" }),
    reconciledAt: Type.Union([Type.String({ format: "date-time" }), Type.Null()]),
    actorId: Type.String({ minLength: 1 }),
    authorizerId: Type.Union([Type.String({ minLength: 1 }), Type.Null()]),
    operatorDecision: Type.Optional(
      Type.Union([GroupBuyOnlineOperatorDecisionSchema, GroupBuyManualOperatorDecisionSchema]),
    ),
    evidence: Type.Union([
      Type.Literal("MANUAL_FALLBACK"),
      Type.Literal("SANDBOX"),
      Type.Literal("STORE_ACCEPTED"),
    ]),
  },
  { additionalProperties: false },
);

export type GroupBuyFulfillmentRecordContract = Static<typeof GroupBuyFulfillmentRecordSchema>;

export function isGroupBuyFulfillmentRecord(
  value: unknown,
): value is GroupBuyFulfillmentRecordContract {
  if (!Value.Check(GroupBuyFulfillmentRecordSchema, value)) return false;
  const record = value as GroupBuyFulfillmentRecordContract;
  const online = record.channelSnapshot.fulfillmentMode === "ONLINE_PROVIDER";
  const operatorMode = record.operatorDecision?.mode;
  if (
    online !== (record.redemptionId !== undefined) ||
    online !== (record.redemptionNumber !== undefined) ||
    (!online && record.redemptionEntryMode !== undefined) ||
    (online &&
      (record.channelSnapshot.officialIntegration === "NOT_VERIFIED" ||
        record.evidence === "MANUAL_FALLBACK" ||
        record.channelSnapshot.officialIntegration !== record.evidence ||
        !isGroupBuyRedemptionNumber(record.redemptionNumber))) ||
    (online &&
      operatorMode !== undefined &&
      operatorMode !== "CASHIER_CONFIRMATION" &&
      operatorMode !== "MANAGER_TAKEOVER") ||
    (!online &&
      operatorMode !== undefined &&
      operatorMode !== "CASHIER_RECORD" &&
      operatorMode !== "MANAGER_RECORD" &&
      operatorMode !== "OWNER_EXCEPTION") ||
    (!online &&
      (record.channelSnapshot.officialIntegration !== "NOT_VERIFIED" ||
        record.evidence !== "MANUAL_FALLBACK"))
  ) {
    return false;
  }
  if (record.inventoryEffects === undefined) return true;
  const productComponents = record.packageSnapshot.components.filter(
    (component) => component.kind === "PRODUCT" && component.referenceId !== null,
  );
  const effects = new Map(record.inventoryEffects.map((effect) => [effect.skuId, effect]));
  return (
    effects.size === record.inventoryEffects.length &&
    effects.size === productComponents.length &&
    productComponents.every((component) => {
      const effect = effects.get(component.referenceId ?? "");
      if (
        !effect ||
        effect.productName !== component.name ||
        effect.quantity !== component.quantity
      ) {
        return false;
      }
      return effect.inventoryManaged
        ? effect.inventoryQuantityBefore !== null &&
            effect.inventoryQuantityAfter === effect.inventoryQuantityBefore - effect.quantity
        : effect.inventoryQuantityBefore === null && effect.inventoryQuantityAfter === null;
    })
  );
}
